import { useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw, Trash2 } from 'lucide-react';
import { fmt } from '@cca/ui';
import type { LineItem } from '../types';
import { TombstoneIcon } from './TombstoneIcon';

type Kind = 'income' | 'expense';

type Props = {
  income: LineItem[];
  expenses: LineItem[];
  onRestore: (kind: Kind, id: string) => void;
  onPurge: (kind: Kind, id: string) => void;
};

const formatArchivedAt = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
};

export function ArchivePanel({ income, expenses, onRestore, onPurge }: Props) {
  const archivedIncome = income.filter((i) => i.deletedAt);
  const archivedExpenses = expenses.filter((e) => e.deletedAt);
  const total = archivedIncome.length + archivedExpenses.length;

  const [open, setOpen] = useState(false);

  if (total === 0) return null;

  return (
    <section className="panel mb-4 no-print">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 text-left py-4 px-1 hover:opacity-70 transition-opacity"
      >
        <span className="flex items-center gap-3">
          <TombstoneIcon className="w-5 h-5 text-ccaanz-grey" />
          <span className="text-[15px] font-medium text-ccaanz-black">Archived rows</span>
          <span className="pill pill-brand">
            {total} {total === 1 ? 'row' : 'rows'}
          </span>
        </span>
        <span className="text-ccaanz-grey">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="border-t border-paper-divider pb-4">
          <p className="py-3 text-[12px] text-ccaanz-grey leading-relaxed">
            Archived rows are excluded from totals, projections, and CSV export but kept on file.{' '}
            <strong className="font-semibold not-italic">Restore</strong> brings a row back into its
            category. <strong className="font-semibold not-italic">Purge</strong> removes it
            permanently on next save.
          </p>
          <div className="space-y-4">
            {archivedIncome.length > 0 && (
              <ArchiveGroup
                heading="Income"
                items={archivedIncome}
                kind="income"
                onRestore={onRestore}
                onPurge={onPurge}
              />
            )}
            {archivedExpenses.length > 0 && (
              <ArchiveGroup
                heading="Expenses"
                items={archivedExpenses}
                kind="expense"
                onRestore={onRestore}
                onPurge={onPurge}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ArchiveGroup({
  heading,
  items,
  kind,
  onRestore,
  onPurge,
}: {
  heading: string;
  items: LineItem[];
  kind: Kind;
  onRestore: (kind: Kind, id: string) => void;
  onPurge: (kind: Kind, id: string) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-gold font-medium mb-2">
        {heading}
      </div>
      <ul className="border border-edge rounded-control overflow-hidden">
        {items.map((item, idx) => (
          <li
            key={item.id}
            className={`px-4 py-3 flex items-center gap-4 ${idx > 0 ? 'border-t border-paper-divider' : ''}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-3">
                <span className="text-[12px] num text-ccaanz-grey w-12 flex-shrink-0">
                  {item.code || '—'}
                </span>
                <span className="text-[13px] font-medium text-ccaanz-black truncate">
                  {item.name}
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-ccaanz-grey">
                Was {fmt(item.currentAmount)} · archived {formatArchivedAt(item.deletedAt)}
                {item.note && (
                  <>
                    {' · '}
                    <span className="italic">{item.note}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => onRestore(kind, item.id)}
                className="btn btn-blue btn-sm"
                title="Restore this row to its original section"
              >
                <RotateCcw className="w-3 h-3" />
                Restore
              </button>
              <button
                type="button"
                onClick={() => onPurge(kind, item.id)}
                className="btn btn-ghost btn-sm"
                title="Permanently delete (cannot be undone after save)"
              >
                <Trash2 className="w-3 h-3" />
                Purge
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
